// The Provider "About us" page, as a Page Builder document. Same visual language as the Seller About
// page (shared kit: pages/shop/aboutDocKit.js) but written for a B2B supplier (producer, manufacturer,
// wholesaler, distributor, dropshipping provider...) and never containing an invented figure:
//   - categories, statistics and rating are LIVE elements bound to the Provider's real data (the
//     Provider store configuration renders them), so the document stays true after it is saved;
//   - "Wholesale Services" / "Dropshipping Services" only show when the Provider really offers them
//     (options.providerRequires), "No reviews yet" only without a real rating (options.providerWhenRating);
//   - no placeholder photo, no private contact data.
// Used as-is when the Provider has not chosen an About page, and as the "Provider About" starting
// template of the Provider My Shop page list (the Provider then edits its own copy in the Builder).
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import { makeNodeId, aboutCard, aboutHeading, aboutText, aboutBadgeGrid } from "../../pages/shop/aboutDocKit";

const live = (type, options, bindSource, style = {}) => ({ id: makeNodeId(`about-${type}`), type, options, bindSource, style: { width: "100%", ...style } });

// `shop`: { name, roleName }. Everything else is resolved live at render time.
export const makeProviderAboutDoc = (shop = {}) => {
  const name = shop.name || text("h_pab_fallback_name", "our business");
  const pageId = makeNodeId("about-page");
  const producer = shop.roleName === "Producer";

  const offers = [
    { icon: "🏭", title: text("h_pab_offer1_t", "Direct from the source"), text: text("h_pab_offer1_x", "Products supplied by the business that makes or sources them.") },
    { icon: "📦", title: text("h_pab_offer2_t", "Volume-ready"), text: text("h_pab_offer2_x", "Minimum order quantities and quantity tiers for professional buyers.") },
    { icon: "🤝", title: text("h_pab_offer3_t", "Seller partnerships"), text: text("h_pab_offer3_x", "Built to supply Elakay Sellers and grow with their shops.") },
    { icon: "🔒", title: text("h_pab_offer4_t", "Secure transactions"), text: text("h_pab_offer4_x", "Orders and payments go through Elakay.") },
  ];
  const quality = [
    { icon: "✅", title: text("h_pab_q1_t", "Accurate listings"), text: text("h_pab_q1_x", "Descriptions, specifications and images that match the product.") },
    { icon: "🔍", title: text("h_pab_q2_t", "Inspection"), text: text("h_pab_q2_x", "Products are checked before they are packed.") },
    { icon: "📋", title: text("h_pab_q3_t", "Consistency"), text: text("h_pab_q3_x", "The same quality from one order to the next.") },
    { icon: "💬", title: text("h_pab_q4_t", "Clear communication"), text: text("h_pab_q4_x", "Order status and tracking shared at every step.") },
  ];

  return {
    meta: { title: text("h_pab_meta_title", "About {value0}", { value0: name }), description: text("h_pab_meta_desc", "Learn more about {value0}.", { value0: name }) },
    pageMode: "store",
    currentPageId: pageId,
    comments: [],
    combinedElements: [],
    pages: [{
      id: pageId,
      name: text("h_pab_page_name", "About Us"),
      style: { maxWidth: "1080px", margin: "0 auto", padding: "32px 16px 48px", minHeight: "60vh" },
      children: [
        // ===== HERO =====
        {
          id: makeNodeId("about-hero"),
          type: "container",
          style: { padding: "56px 28px", borderRadius: "24px", textAlign: "center", color: "#ffffff", background: "linear-gradient(135deg, #007399, #0ea5b7 55%, #1d4ed8)", marginBottom: "24px" },
          children: [
            aboutHeading(text("h_pab_welcome", "Welcome to {value0}", { value0: name }), { tag: "h1", color: "#ffffff", size: "40px", align: "center", style: { margin: "0 0 12px" } }),
            aboutText(producer
              ? text("h_pab_tagline_producer", "A producer supplying Elakay Sellers with products made at the source.")
              : text("h_pab_tagline", "A professional supplier for Elakay Sellers: wholesale, dropshipping and reliable fulfillment."),
            { maxWidth: "680px", margin: "0 auto", color: "#e0f7fb", fontSize: "18px", textAlign: "center" }),
          ],
        },

        // ===== REAL FIGURES (products, wholesale, dropshipping, connected Sellers, rating, likes) =====
        live("shopHeaderSeller", { providerSection: "stats" }, "store.provider", { marginBottom: "24px" }),

        // ===== ABOUT THE PROVIDER =====
        aboutCard([
          aboutHeading(text("h_pab_about_title", "About the Provider")),
          aboutText(text("h_pab_about_p1", "Welcome to {value0}. We supply products to businesses selling on Elakay, whether they buy in volume or sell our products in their own shop.", { value0: name })),
          aboutText(text("h_pab_about_p2", "Our shop is part of the Elakay marketplace, which connects producers, manufacturers, wholesalers, distributors and Sellers through one trusted platform."), { margin: 0 }),
        ]),

        // ===== STORY / MISSION / VISION =====
        aboutCard([
          aboutHeading(text("h_pab_story_title", "Our Story")),
          aboutText(text("h_pab_story_p1", "We started with one goal: give Sellers direct access to quality products at fair professional prices, without unnecessary intermediaries.")),
          aboutHeading(text("h_pab_mission_title", "Our Mission"), { tag: "h3", size: "20px" }),
          aboutText(text("h_pab_mission_p1", "Supply our partners with reliable products, honest information and dependable fulfillment, so they can build profitable shops.")),
          aboutHeading(text("h_pab_vision_title", "Our Vision"), { tag: "h3", size: "20px" }),
          aboutText(text("h_pab_vision_p1", "Become a long-term source that Sellers trust for their catalogue, their stock and their growth."), { margin: 0 }),
        ]),

        // ===== WHAT WE SUPPLY (real categories) =====
        aboutCard([
          aboutHeading(text("h_pab_supply_title", "Products We Supply")),
          aboutText(text("h_pab_supply_p1", "Our catalogue is organised in the following categories:")),
          live("shopCatMatrix", { title: "" }, "store.categories"),
          aboutBadgeGrid(offers),
        ]),

        // ===== WHOLESALE (only when really offered) =====
        { ...aboutCard([
          aboutHeading(text("h_pab_ws_title", "Wholesale Services")),
          aboutText(text("h_pab_ws_p1", "Buy in volume directly from us. Each product shows its minimum order quantity and, when available, its quantity price tiers.")),
          aboutText(text("h_pab_ws_p2", "Stock, minimum quantities and delivery regions are checked on every order before it is confirmed."), { margin: 0 }),
        ]), options: { providerRequires: "wholesale" } },

        // ===== DROPSHIPPING (only when really offered) =====
        { ...aboutCard([
          aboutHeading(text("h_pab_ds_title", "Dropshipping Services")),
          aboutText(text("h_pab_ds_p1", "Add our products to your Elakay shop with “Add to My Shop”: you set your price, we prepare and ship your customers' orders.")),
          aboutText(text("h_pab_ds_p2", "Product information and availability stay synchronized with our catalogue."), { margin: 0 }),
        ]), options: { providerRequires: "dropshipping" } },

        // ===== SELLER PARTNERSHIPS =====
        aboutCard([
          aboutHeading(text("h_pab_partners_title", "Seller Partnerships")),
          aboutText(text("h_pab_partners_p1", "We work with Sellers as partners: clear conditions, consistent quality and communication through Elakay at every step of an order."), { margin: 0 }),
        ]),

        // ===== HOW WE WORK =====
        aboutCard([
          aboutHeading(text("h_pab_work_title", "How We Work")),
          aboutHeading(text("h_pab_orders_title", "Order Processing"), { tag: "h3", size: "20px" }),
          aboutText(text("h_pab_orders_list", "Every order is reviewed and prepared:\n• Stock verification\n• Product inspection\n• Professional packaging\n• Shipping coordination\n• Tracking updates")),
          aboutHeading(text("h_pab_ship_title", "Shipping & Delivery"), { tag: "h3", size: "20px" }),
          aboutText(text("h_pab_ship_p1", "Delivery times depend on the destination, the carrier and the quantity ordered. Tracking information is shared as soon as an order ships.")),
          aboutHeading(text("h_pab_quality_title", "Quality Standards"), { tag: "h3", size: "20px" }),
          aboutBadgeGrid(quality),
        ]),

        // ===== SECURE BUSINESS =====
        aboutCard([
          aboutHeading(text("h_pab_secure_title", "Secure Business on Elakay")),
          aboutText(text("h_pab_secure_p1", "Orders placed with us go through Elakay's secure payment processing and marketplace protection, for us and for our partners."), { margin: 0 }),
        ]),

        // ===== RATINGS (real rating, or an honest "No reviews yet") =====
        aboutCard([
          aboutHeading(text("h_pab_ratings_title", "Ratings")),
          { id: makeNodeId("about-rating"), type: "rating", content: { value: 0, max: 5, color: "#f59e0b", label: text("h_pab_ratings_label", "Average rating from Elakay customers") }, style: { display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }, options: { providerWhenRating: "some" } },
          { ...aboutText(text("h_pab_no_reviews", "No reviews yet."), { margin: 0, textAlign: "center", color: "#64748b" }), options: { providerWhenRating: "none" } },
        ]),

        // ===== RETURNS =====
        aboutCard([
          aboutHeading(text("h_pab_returns_title", "Returns and Resolution")),
          aboutText(text("h_pab_returns_p1", "If an order arrives damaged, incomplete or incorrect, contact us through the order on Elakay. We work toward a fair resolution according to Elakay's marketplace policies."), { margin: 0 }),
        ]),

        // ===== CONTACT (no private data: every exchange goes through Elakay) =====
        aboutCard([
          aboutHeading(text("h_pab_contact_title", "Contact Information")),
          aboutText(text("h_pab_contact_p1", "Questions about a product, a wholesale order or a dropshipping partnership? Reach us through your Elakay orders and the Elakay Help Center."), { margin: 0 }),
        ]),

        // ===== COMMUNITY =====
        aboutCard([
          aboutHeading(text("h_pab_community_title", "Supporting Communities")),
          aboutText(text("h_pab_community_p1", "By supplying local and independent Sellers, we help create economic opportunities for entrepreneurs and their communities."), { margin: 0 }),
        ]),

        // ===== THANK YOU =====
        {
          id: makeNodeId("about-thanks"),
          type: "container",
          style: { padding: "40px 28px", borderRadius: "20px", textAlign: "center", color: "#ffffff", background: "linear-gradient(135deg, #075985, #007399)" },
          children: [
            aboutHeading(text("h_pab_thanks_title", "Thank You"), { tag: "h2", color: "#ffffff", size: "30px", align: "center" }),
            aboutText(text("h_pab_thanks_p1", "Thank you for considering {value0} as your supplier. Your success is our priority.", { value0: name }), { color: "#e0f7fb", textAlign: "center", maxWidth: "640px", margin: "0 auto" }),
          ],
        },
      ],
    }],
  };
};

// First page of the generated About document, for the "Provider About" page template.
export const providerAboutTemplatePage = (shop, pageName) => {
  const page = makeProviderAboutDoc(shop).pages[0];
  return { ...page, name: pageName || page.name };
};
